import CircularProgress from '@mui/material/CircularProgress';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import React, { useState, useEffect } from 'react';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { Provider as StoreProvider } from 'react-redux';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import './assets/css/google.css';
import './assets/scss/main.scss';
import GoogleAnalytics from './components/GoogleAnalytics';
import ScrollReset from './components/ScrollReset';
import StylesProvider from './components/StylesProvider';
import './modules/mixins/chartjs';
import './modules/mixins/moment';
import './modules/mixins/prismjs';
import './modules/mixins/validate';
import { configureStore } from './store';
import { theme } from './modules/theme';
import { Security, LoginCallback } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import Auth from './layouts/Auth';
import { oktaIssuer, oktaClientId } from 'src/utils/contants';
import ChatbotIframe from 'src/components/Chatbot/ChatbotIframe';
import { getUser } from 'src/utils/Common'; // Import getUser to access user details
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { HelmetProvider } from 'react-helmet-async';

const { store, persistor } = configureStore();



const config = {
  issuer: oktaIssuer,
  redirectUri: `${window.location.origin}/implicit/callback`,
  clientId: oktaClientId,
  pkce: true,
  scopes: ['openid', 'email', 'profile', 'offline_access'], // refresh_token için offline_access eklendi
  cookies: {
    secure: true,
    sameSite: 'None', // Çerezlerin PWA'da çalışmasını sağlar
  },
  tokenManager: {
    storage: 'localStorage', // Alternatif: sessionStorage veya özel secureStorage
    autoRenew: true,
    autoRemove: true,
    secure: true, // Token'ların güvenli saklanması için
    expireEarlySeconds: 60, // Token süresinin dolmasına 30 saniye kala yenile
  },
  disableHttpsCheck: false, // HTTPS zorunluluğunu açık tut
};



const oktaAuth = new OktaAuth(config);

function AppRoutes({ sessionUser, chatbotOpen, handleChatbotOpen, handleChatbotClose }) {
  const navigate = useNavigate();

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri, window.location.origin), { replace: true });
  };

  return (
    <>
      <ScrollReset />
      <GoogleAnalytics />
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <Routes>
          <Route path="/implicit/callback" element={<LoginCallback />} />
          <Route path="/*" element={<Auth />} />
        </Routes>
        {sessionUser?.Role_Id === 10 && (
          <div
            style={{
              position: 'fixed',
              bottom: '60px',
              right: '20px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                backgroundColor: '#FF9800',
                color: '#FFFFFF',
                padding: '12px 16px',
                borderRadius: '16px',
                fontSize: '16px',
                fontWeight: 'bold',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'inline-block',
                position: 'relative',
                marginBottom: '10px',
              }}
            >
              Merhaba, Asistan 360° sorularınızı yanıtlamaya hazır!
              <div
                style={{
                  content: '""',
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '0',
                  height: '0',
                  borderLeft: '14px solid transparent',
                  borderRight: '14px solid transparent',
                  borderTop: '14px solid #FF9800',
                }}
              />
            </div>
            <button
              style={{
                backgroundColor: '#FF9800',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '50%',
                width: '80px',
                height: '80px',
                cursor: 'pointer',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              }}
              onClick={handleChatbotOpen}
            >
              <ChatBubbleIcon style={{ fontSize: '35px' }} />
            </button>
          </div>
        )}
        <ChatbotIframe open={chatbotOpen} onClose={handleChatbotClose} />
      </Security>
    </>
  );
}

function App() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [sessionUser, setSessionUser] = useState(null);
  const [loading, setLoading] = useState(true); // Add loading state

  const handleChatbotOpen = () => {
    setChatbotOpen(true);
  };

  const handleChatbotClose = () => {
    setChatbotOpen(false);
  };

  // localStorage temizlenirken korunacak kullanıcı tercihleri (ör. kılavuz üst grup sırası)
  const clearStorageKeepPrefs = () => {
    const PRESERVE = ['EKIPMAN_KILAVUZ_UST_SIRA'];
    try {
      const saved = {};
      PRESERVE.forEach(k => { const v = localStorage.getItem(k); if (v != null) saved[k] = v; });
      localStorage.clear();
      Object.keys(saved).forEach(k => localStorage.setItem(k, saved[k]));
    } catch (e) {
      try { localStorage.clear(); } catch (_) { /* yok say */ }
    }
  };

  useEffect(() => {
    if (isLoaded) {
      clearStorageKeepPrefs();
    }
  }, [isLoaded]);

  useEffect(() => {
    const user = getUser();
    setSessionUser(user);
    setLoading(false); // Once user data is fetched, set loading to false
  }, []);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', () => { clearStorageKeepPrefs(); });
  }, []);

  // If loading, show spinner
  if (loading) {
    return <CircularProgress />;
  }

  return (
    <HelmetProvider>
      <StoreProvider store={store}>
        <PersistGate loading={<CircularProgress status={'loading'} />} persistor={persistor}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <StylesProvider direction="ltr">
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <BrowserRouter>
                    <AppRoutes
                      sessionUser={sessionUser}
                      chatbotOpen={chatbotOpen}
                      handleChatbotOpen={handleChatbotOpen}
                      handleChatbotClose={handleChatbotClose}
                    />
                  </BrowserRouter>
                </LocalizationProvider>
              </StylesProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </PersistGate>
      </StoreProvider>
    </HelmetProvider>
  );
}

export default App;
